import { createFileRoute, Link } from "@tanstack/react-router";
import { tools, categories } from "@/lib/tools";
import { ToolCard } from "@/components/ToolCard";
import { BookOpen, Wrench, Sparkles, Shield } from "lucide-react";

const FAQS = [
  {
    q: "As ferramentas para MEI do MEI Toolbox são realmente gratuitas?",
    a: "Sim. Todas as ferramentas — calculadora DAS MEI, validador de CNPJ, gerador de recibo, precificação, contratos PDF e demais utilitários — são 100% gratuitas, não exigem cadastro e podem ser usadas quantas vezes você precisar.",
  },
  {
    q: "Preciso me cadastrar para usar as ferramentas para microempreendedor individual?",
    a: "Não. Você abre a ferramenta, preenche os dados e usa. Nada do que você digita é enviado para nossos servidores: o cálculo roda no seu próprio navegador.",
  },
  {
    q: "Qual a melhor ferramenta para calcular o DAS do MEI?",
    a: "A Calculadora DAS MEI do MEI Toolbox calcula o valor do mês com base na tabela vigente, simula atrasos com multa e juros, e mostra quanto vai para INSS, ISS e ICMS — tudo gratuito.",
  },
  {
    q: "Tem ferramenta para simular o limite de faturamento do MEI?",
    a: "Sim. O simulador de limite de faturamento avisa quando você está se aproximando dos R$ 81.000 anuais e mostra o que acontece se ultrapassar o teto.",
  },
  {
    q: "As ferramentas funcionam no celular?",
    a: "Sim. Todas as ferramentas do MEI Toolbox são responsivas e funcionam em celular, tablet e computador.",
  },
];

export const Route = createFileRoute("/ferramentas/")({
  head: () => ({
    meta: [
      { title: "Ferramentas MEI 2026 Grátis: DAS, CNPJ, Recibo e Mais" },
      {
        name: "description",
        content:
          "Mais de 30 ferramentas grátis para MEI: calculadora DAS-MEI, validador de CNPJ, gerador de recibo, precificação e contratos em PDF. Sem cadastro, 100% online.",
      },
      { property: "og:title", content: "Ferramentas Grátis para MEI 2026 | MEI Toolbox" },
      {
        property: "og:description",
        content:
          "Calculadoras, validadores, geradores e utilitários gratuitos para microempreendedores individuais.",
      },
      { property: "og:url", content: "https://meitoolbox.com.br/ferramentas" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Ferramentas Grátis para MEI 2026 | MEI Toolbox" },
      {
        name: "twitter:description",
        content:
          "Calculadora DAS, validador de CNPJ, gerador de recibo e mais. Tudo grátis e online.",
      },
    ],
    links: [{ rel: "canonical", href: "https://meitoolbox.com.br/ferramentas" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Início",
              item: "https://meitoolbox.com.br/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Ferramentas",
              item: "https://meitoolbox.com.br/ferramentas",
            },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Todas as ferramentas grátis para MEI",
          url: "https://meitoolbox.com.br/ferramentas",
          inLanguage: "pt-BR",
          isPartOf: { "@id": "https://meitoolbox.com.br/#website" },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: ToolsIndex,
});

function ToolsIndex() {
  const available = tools.filter((t) => t.available).length;
  const upcoming = tools.filter((t) => !t.available).length;

  return (
    <div className="container mx-auto px-4 py-12 md:py-16">
      <header className="max-w-3xl mb-12">
        <div className="text-xs font-bold uppercase tracking-widest text-primary mb-2">
          Catálogo de ferramentas para MEI
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-bold leading-tight">
          Ferramentas grátis para MEI
        </h1>
        <p className="mt-4 text-lg text-muted-foreground">
          {available} ferramentas para <strong>microempreendedor individual</strong> disponíveis
          hoje, mais {upcoming} chegando em breve. Calcule DAS, valide CNPJ, gere recibos e simule o
          limite de faturamento sem pagar nada.
        </p>
        <div className="mt-5 flex flex-wrap gap-2 text-xs">
          <span className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 font-semibold">
            <Sparkles className="h-3 w-3" /> 100% grátis
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 font-semibold">
            <Shield className="h-3 w-3" /> Sem cadastro
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 font-semibold">
            <Wrench className="h-3 w-3" /> Pronto para usar
          </span>
        </div>
      </header>

      {categories.map((cat) => {
        const list = tools.filter((t) => t.category === cat.id);
        if (list.length === 0) return null;
        return (
          <section key={cat.id} className="mb-12">
            <h2 className="font-display text-xl font-bold mb-5 flex items-center gap-3">
              <span
                className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${cat.color}`}
              >
                {cat.label}
              </span>
            </h2>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {list.map((t) => (
                <ToolCard key={t.slug} tool={t} />
              ))}
            </div>
          </section>
        );
      })}

      <section className="prose prose-lg max-w-none dark:prose-invert mt-16 border-t border-border pt-12">
        <h2 className="font-display text-3xl font-bold">
          A caixa de ferramentas do microempreendedor individual
        </h2>
        <p>
          Ser <strong>MEI</strong> é, na prática, ser sócio, vendedor, financeiro e contador do
          próprio negócio. O <strong>MEI Toolbox</strong> nasceu para tirar essa sobrecarga:
          reunimos em um só lugar as <strong>ferramentas gratuitas para MEI</strong> que substituem
          aquelas planilhas confusas, sites travados e simuladores incompletos espalhados pela
          internet. Tudo aqui foi pensado para resolver, em poucos cliques, as tarefas que tomam o
          tempo do microempreendedor individual brasileiro.
        </p>

        <h2 className="font-display text-3xl font-bold">O que você consegue resolver aqui</h2>
        <h3 className="font-semibold text-xl">Gestão financeira do MEI</h3>
        <p>
          Calcule o <strong>DAS MEI</strong> do mês com a tabela 2026 atualizada, simule atrasos com
          multa e juros, controle quanto entrou e saiu no mês, simule o{" "}
          <strong>limite de faturamento</strong> dos R$ 81.000 anuais e descubra se está perto de
          ultrapassar o teto antes que vire problema com a Receita Federal. A nossa ferramenta de
          precificação mostra qual o preço justo de produto e serviço considerando custos, impostos
          e margem.
        </p>

        <h3 className="font-semibold text-xl">Documentos prontos em PDF</h3>
        <p>
          Gere <strong>recibo de pagamento</strong>,{" "}
          <strong>contratos de prestação de serviço</strong>, propostas comerciais e declarações em
          segundos. Todos saem em PDF com a sua marca (nome, CNPJ, logo) — basta configurar uma vez
          na página <Link to="/marca">Sua marca nos PDFs</Link> e usar em qualquer ferramenta.
        </p>

        <h3 className="font-semibold text-xl">Validadores e consultas</h3>
        <p>
          <strong>Validador de CNPJ</strong>, conferência de situação cadastral, busca de CNAEs
          permitidos para MEI e checagem rápida antes de fechar negócio com cliente ou fornecedor.
          Útil para quem trabalha com vendas, prestação de serviço a empresas e quem está abrindo o
          primeiro CNPJ.
        </p>

        <h3 className="font-semibold text-xl">Assistente IA do MEI</h3>
        <p>
          Tire dúvidas sobre{" "}
          <strong>
            DAS, nota fiscal, INSS, Simples Nacional, formalização e obrigações mensais
          </strong>{" "}
          com um assistente treinado especialmente para microempreendedores individuais. Respostas
          diretas, sem juridiquês.
        </p>

        <h2 className="font-display text-3xl font-bold">
          Por que escolher as ferramentas do MEI Toolbox
        </h2>
        <ul>
          <li>
            <strong>100% gratuitas</strong>: não há plano pago escondido, paywall ou limite de uso.
          </li>
          <li>
            <strong>Sem cadastro</strong>: você abre a ferramenta e usa. Nada de e-mail, senha ou
            login social.
          </li>
          <li>
            <strong>Privacidade real</strong>: cálculos rodam no seu navegador. CNPJ, faturamento e
            dados financeiros não saem do seu aparelho.
          </li>
          <li>
            <strong>Atualizadas</strong>: valores do DAS 2026, novo CRT 4 da NFS-e, tabela do INSS e
            regras do Simples Nacional sempre em dia.
          </li>
          <li>
            <strong>Responsivas</strong>: funcionam em celular, tablet e computador.
          </li>
          <li>
            <strong>Feitas para o MEI brasileiro</strong>: cada ferramenta resolve uma dor real do
            microempreendedor individual.
          </li>
        </ul>

        <h2 className="font-display text-3xl font-bold">Para quem servem essas ferramentas</h2>
        <p>O catálogo atende todos os perfis que compõem o ecossistema MEI no Brasil:</p>
        <ul>
          <li>
            <strong>Prestadores de serviço</strong> (cabeleireiros, manicures, eletricistas,
            pedreiros, desenvolvedores, designers).
          </li>
          <li>
            <strong>Comerciantes e vendedores online</strong> que precisam emitir nota e controlar
            faturamento.
          </li>
          <li>
            <strong>Profissionais autônomos</strong> recém-formalizados que ainda estão entendendo
            as obrigações do CNPJ.
          </li>
          <li>
            <strong>MEIs experientes</strong> que querem otimizar tempo nas tarefas mensais.
          </li>
          <li>
            <strong>Contadores e escritórios parceiros</strong> que indicam ferramentas para seus
            clientes.
          </li>
        </ul>

        <h2 className="font-display text-3xl font-bold">Como começar</h2>
        <ol>
          <li>
            Escolha a categoria que resolve sua dor de hoje (Tributos, Documentos, Validadores,
            Financeiro).
          </li>
          <li>
            Abra a ferramenta e preencha os campos. Nada é enviado para fora do seu navegador.
          </li>
          <li>Baixe o PDF, copie o resultado ou exporte para a sua agenda.</li>
          <li>
            Se quiser personalizar PDFs com a sua marca, configure em{" "}
            <Link to="/marca">Sua marca</Link>.
          </li>
          <li>
            Consulte o histórico do que você já gerou em <Link to="/historico">Meu histórico</Link>.
          </li>
        </ol>

        <h2 className="font-display text-3xl font-bold">
          Perguntas frequentes sobre as ferramentas para MEI
        </h2>
        {FAQS.map((f) => (
          <div key={f.q}>
            <h3 className="font-semibold text-xl">{f.q}</h3>
            <p>{f.a}</p>
          </div>
        ))}
      </section>

      <section className="mt-16 rounded-2xl border border-border bg-gradient-hero text-white p-8 md:p-12 text-center">
        <h2 className="font-display text-2xl md:text-3xl font-bold mb-3">
          Comece pela ferramenta mais usada pelos MEIs
        </h2>
        <p className="text-white/85 mb-6 max-w-xl mx-auto">
          A Calculadora DAS MEI 2026 calcula o boleto do mês, simula atrasos e mostra o destino de
          cada centavo — tudo grátis.
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link
            to="/ferramentas/$slug"
            params={{ slug: "calculadora-das-mei" }}
            className="inline-flex items-center gap-2 rounded-full bg-white text-primary px-6 py-2.5 text-sm font-bold hover:opacity-90 transition"
          >
            <Wrench className="h-4 w-4" /> Abrir calculadora DAS
          </Link>
          <Link
            to="/artigos"
            className="inline-flex items-center gap-2 rounded-full border border-white/30 px-6 py-2.5 text-sm font-bold hover:bg-white/10 transition"
          >
            <BookOpen className="h-4 w-4" /> Ler artigos sobre MEI
          </Link>
        </div>
      </section>
    </div>
  );
}
